import type { AxiosError } from 'axios';

import { Logger } from '../../logger';
import { buildEnergyManagerClient } from '../apiClient';
import {
    CreateSubscription,
    Entitlement,
    GetEntitlementForLicenseIdArgs,
    Subscription,
    SubscriptionAuditRecord,
} from './types';

export const getSubscriptionOfOrganization = async (organizationId: string): Promise<Subscription | undefined> => {
    try {
        const apiClient = buildEnergyManagerClient();

        const response = await apiClient.get<Subscription>(`/api/subscriptions?organizationId=${organizationId}`);

        return response.data;
    } catch {
        return undefined;
    }
};

export const getSubscriptionAuditOfOrganization = async (
    organizationId: string
): Promise<SubscriptionAuditRecord[]> => {
    const apiClient = buildEnergyManagerClient();

    const response = await apiClient.get<SubscriptionAuditRecord[]>(
        `/api/subscriptions/audit?organizationId=${organizationId}`
    );

    return response.data;
};

export const validateEntitlement = async (args: GetEntitlementForLicenseIdArgs): Promise<Entitlement | undefined> => {
    try {
        const apiClient = buildEnergyManagerClient();

        const response = await apiClient.get<{ entitlement: Entitlement[] }>(
            `/api/subscriptions/${args.licenseId}/validate-entitlement`
        );

        if (response.status === 200) {
            // API returns { entitlement: Entitlement[] } structure
            if (response.data?.entitlement) {
                if (Array.isArray(response.data.entitlement)) {
                    return response.data.entitlement[0]; // Return first entitlement
                }
                return response.data.entitlement as Entitlement;
            }
            Logger.error('No entitlement data in response');
            return undefined;
        }
        Logger.error(`Unexpected response status: ${response.status}`);
        return undefined;
    } catch (error) {
        const { config, response } = error as AxiosError;

        Logger.info(
            `Error in validateEntitlement service call: ${JSON.stringify({
                params: config?.params,
                status: response?.status,
                statusText: response?.statusText,
                responseData: response?.data,
                errorMessage: error instanceof Error ? error.message : String(error),
            })}`
        );
        // Create enhanced error with specific message from response
        let errorMessage = error instanceof Error ? error.message : String(error);

        // Extract specific message from response data if available
        const responseData = response?.data as any;
        if (responseData?.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
            errorMessage = responseData.errors[0].message ?? errorMessage;
        } else if (responseData?.message) {
            errorMessage = responseData.message;
        }

        const enhancedError = new Error(errorMessage);
        (enhancedError as any).responseData = response?.data;
        (enhancedError as any).status = response?.status;
        (enhancedError as any).statusText = response?.statusText;
        (enhancedError as any).originalError = error;

        throw enhancedError;
    }
};

export const createSubscription = async (args: CreateSubscription): Promise<void> => {
    const apiClient = buildEnergyManagerClient();
    await apiClient.post<void>(`/api/subscriptions`, args);
};

export const SubscriptionService = {
    getSubscriptionOfOrganization,
    getSubscriptionAuditOfOrganization,
    validateEntitlement,
    createSubscription,
};
